// The all-centers day (SPEC §7.2): "Needs attention" first, then every center as a grid (classrooms side by side,
// grouped under their center) or as a dense list grouped by center.

import type { AttentionItem, CenterDay, ClassroomColumn, ConflictItem, Lesson, SchoolDay } from "../../api/types";
import { t, translateCode } from "../../i18n/i18n";
import { formatRange } from "../../time";
import { createElement, createLink } from "../../ui/dom";
import { classroomToColumn } from "../../ui/gridColumns";
import { describeDataProblems, describeLessonFlags, lessonRoute } from "../../ui/lessonLabels";
import { buildTimeline, type TimelineColumn } from "../../ui/timeline";

export type DayLayout = "grid" | "list";

/** Attention banner, closures, then the grid or the list. */
export function buildAllCentersDay(day: SchoolDay, layout: DayLayout): HTMLElement[] {
  const parts: HTMLElement[] = [buildAttention(day.attention), ...buildClosureBanners(day.centers)];
  parts.push(layout === "grid" ? buildAllCentersGrid(day.centers) : buildAllCentersList(day.centers));
  return parts;
}

/** Lessons whose teacher or room is unavailable, double bookings and data problems, each linked (SPEC §7.2). */
export function buildAttention(items: AttentionItem[]): HTMLElement {
  if (items.length === 0) {
    return createElement("p", "attention-ok", "✓ " + t("day.nothingNeedsAttention"));
  }

  const box: HTMLElement = createElement("section", "attention");
  box.setAttribute("aria-label", t("day.attentionTitle"));
  box.append(createElement("h2", "", `⚠ ${t("day.attentionTitle")} (${String(items.length)})`));
  const list: HTMLUListElement = createElement("ul");
  for (const item of items) {
    list.append(buildAttentionItem(item));
  }

  box.append(list);
  return box;
}

function buildAttentionItem(item: AttentionItem): HTMLLIElement {
  const lesson: Lesson = item.lesson;
  const entry: HTMLLIElement = createElement("li");
  const label: string = `${formatRange(lesson.range)} ${lesson.groupName} · ${lesson.centerName}, ${
    lesson.classroomName} · ${lesson.teacherName}`;
  const problems: string[] = [
    ...item.conflicts.map((conflict: ConflictItem): string => translateCode("conflicts", conflict.code,
      conflict.params)),
    ...describeDataProblems(lesson),
  ];
  entry.append(createLink(lessonRoute(lesson), label), createElement("span", "attention-reasons",
    ` — ${problems.join("; ")}`));
  return entry;
}

function buildClosureBanners(centers: CenterDay[]): HTMLElement[] {
  return centers
    .filter((center: CenterDay): boolean => center.closure !== null)
    .map((center: CenterDay): HTMLElement => {
      const reason: string = translateCode("closureReasons", center.closure?.reason ?? "Other");
      return createElement("div", "banner", `${center.centerName}: ${t("day.closed", { reason })}`);
    });
}

/** Every classroom of every center side by side, the center's name over its first classroom. */
export function buildAllCentersGrid(centers: CenterDay[]): HTMLElement {
  const columns: TimelineColumn[] = centers.flatMap((center: CenterDay): TimelineColumn[] =>
    center.columns.map((column: ClassroomColumn): TimelineColumn => classroomToColumn(column, center.centerName)));
  if (columns.length === 0) {
    return createElement("p", "muted", t("day.noClassrooms"));
  }

  return buildTimeline(columns, "dense");
}

/** One dense table per center, lessons in time order (SPEC §7.2 "List"). */
export function buildAllCentersList(centers: CenterDay[]): HTMLElement {
  const wrapper: HTMLDivElement = createElement("div", "day-list");
  for (const center of centers) {
    const lessons: Lesson[] = listCenterLessons(center);
    const section: HTMLElement = createElement("section", "day-list-center");
    section.append(createElement("h2", "", center.centerName));
    section.append(lessons.length === 0 ? createElement("p", "muted", t("day.noLessons")) : buildLessonTable(lessons));
    wrapper.append(section);
  }

  return wrapper;
}

/** The center's lessons from all its classrooms, by start time, then room. */
export function listCenterLessons(center: CenterDay): Lesson[] {
  const lessons: Lesson[] = center.columns.flatMap((column: ClassroomColumn): Lesson[] => column.grid.lessons);
  return lessons.sort((first: Lesson, second: Lesson): number =>
    first.range.start - second.range.start || first.classroomName.localeCompare(second.classroomName));
}

function buildLessonTable(lessons: Lesson[]): HTMLElement {
  const table: HTMLTableElement = createElement("table", "dense-table day-list-table");
  // Fixed column widths, so the tables of all centers line up under each other.
  const columns: HTMLTableColElement = createElement("colgroup");
  for (const name of ["time", "group", "course", "teacher", "room", "flags"]) {
    columns.append(createElement("col", `day-list-${name}`));
  }

  table.append(columns);
  const head: HTMLTableRowElement = table.createTHead().insertRow();
  for (const title of [t("app.time"), t("lesson.group"), t("lesson.course"), t("lesson.teacher"), t("lesson.room"),
    ""]) {
    head.append(createElement("th", "", title));
  }

  const body: HTMLTableSectionElement = table.createTBody();
  for (const lesson of lessons) {
    body.append(buildLessonRow(lesson));
  }

  const scroller: HTMLDivElement = createElement("div", "table-scroll");
  scroller.append(table);
  return scroller;
}

function buildLessonRow(lesson: Lesson): HTMLTableRowElement {
  const row: HTMLTableRowElement = createElement("tr", lesson.takesPlace ? "" : "is-not-taking-place");
  const group: HTMLTableCellElement = createElement("td", "nowrap");
  group.append(createLink(lessonRoute(lesson), lesson.groupName));
  const flags: HTMLTableCellElement = createElement("td");
  for (const label of [...describeLessonFlags(lesson)]) {
    flags.append(createElement("span", "badge badge-flag", label));
  }

  row.append(createElement("td", "nowrap", formatRange(lesson.range)), group,
    createElement("td", "", lesson.courseName), buildTeacherCell(lesson),
    createElement("td", "", lesson.classroomName), flags);
  return row;
}

/** The teacher who teaches it, linked to their week; a substitute also names the main teacher. */
function buildTeacherCell(lesson: Lesson): HTMLTableCellElement {
  const cell: HTMLTableCellElement = createElement("td");
  cell.append(createLink(`/teachers/${lesson.teacherId}?from=${lesson.key.date}`, lesson.teacherName));
  if (lesson.isSubstituted) {
    cell.append(` (${t("day.insteadOf", { name: lesson.mainTeacherName })})`);
  }

  return cell;
}
